import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { fetchAllPaginated } from '@/lib/supabase-paginate';

// Re-eksporter mutations så eksisterende imports fortsetter å fungere (VIB-653).
export * from './use-task-mutations';

export interface MyTask {
  id: string; // budget_line_assignment id
  /**
   * VIB-1967: eksplisitt tildelings-id. Bruk ALLTID denne, aldri `id`, når du
   * skal persistere `time_entries.assignment_id`.
   *
   * `id` er assignment-id bare for ekte oppgaver herfra. «Ny registrering»
   * bygger også MyTask-objekter for interne prosjekter (`id` = prosjekt-id),
   * interne oppgaver (`id` = budsjettlinje-id) og reisetid (`id` =
   * "reise:<prosjekt-id>"). De lar dette feltet stå udefinert, slik at en
   * ukoblet føring blir `null` i stedet for en id som peker på feil tabell.
   */
  assignment_id?: string | null;
  budget_line_id: string; // budget line id (for FK-based matching)
  project_id: string;
  task_description: string;
  task_status: string;
  phase: string;
  project_name: string;
  assigned_hours: number;
  logged_hours: number;
  rejection_reason?: string;
  project_status?: string;
  estimated_deadline?: string;
  notes?: string;
  is_internal?: boolean;
  project_type?: string | null;
  is_reisetid?: boolean; // VIB-909: free reisetid logging, line resolved via RPC
  is_project_lead?: boolean; // VIB-956: aktiv bruker er PL på oppgavens prosjekt
}

export interface TaskItem {
  id: string;
  description: string;
  phase: string;
  projectName: string;
  assignedHours: number;
  loggedHours: number;
  status: string;
}

export interface InternalProject {
  id: string;
  name: string;
}

export interface ReiseProject {
  id: string;
  name: string;
  project_code: string | null;
}

export interface UseMyTasksResult {
  tasks: MyTask[];
  taskItems: TaskItem[]; // Legacy interface for backward compat
  internalProjects: InternalProject[];
  reiseProjects: ReiseProject[];
}

export function useMyTasks(userId: string | null) {
  return useQuery({
    queryKey: ['my-tasks', userId],
    queryFn: async (): Promise<UseMyTasksResult> => {
      if (!userId) return { tasks: [], taskItems: [], internalProjects: [], reiseProjects: [] };

      const { data: assignments, error } = await supabase
        .from('budget_line_assignments')
        .select('id, budget_line_id, hours, label, task_status, rejection_reason, estimated_deadline, notes, budget_lines(id, description, phase, project_id, projects(id, name, project_status, is_internal, project_type, production_lead_id))')
        .eq('user_id', userId)
        .in('task_status', ['in_progress', 'not_started', 'completed', 'rejected', 'approved']);
      if (error) throw error;

      // Paginate: all-time per-user time_entries grows past the 1000-row cap
      // (busiest user is at ~870 already). See VIB-1249.
      const timeEntries = await fetchAllPaginated<{
        task_description: string | null;
        duration_seconds: number | null;
        budget_line_id: string | null;
        assignment_id: string | null;
      }>(
        'time_entries',
        'task_description, duration_seconds, budget_line_id, assignment_id',
        (q) => q.eq('user_id', userId).is('deleted_at', null),
      );

      // VIB-671: prioritize assignment_id-based summering. Fall back to
      // budget_line_id only when no entries on that bl have assignment_id set
      // (pre-VIB-671 legacy data).
      const loggedByAssignmentMap = new Map<string, number>();
      const loggedByBLMap = new Map<string, number>();
      const blsWithAssignmentEntries = new Set<string>();
      // VIB-1967: føringer uten assignment_id, per budsjettlinje.
      const orphanByBLMap = new Map<string, number>();

      for (const te of timeEntries) {
        const seconds = te.duration_seconds ?? 0;
        if (te.assignment_id) {
          loggedByAssignmentMap.set(
            te.assignment_id,
            (loggedByAssignmentMap.get(te.assignment_id) ?? 0) + seconds / 3600,
          );
          if (te.budget_line_id) blsWithAssignmentEntries.add(te.budget_line_id);
        } else if (te.budget_line_id) {
          orphanByBLMap.set(
            te.budget_line_id,
            (orphanByBLMap.get(te.budget_line_id) ?? 0) + seconds / 3600,
          );
        }
        if (te.budget_line_id) {
          loggedByBLMap.set(
            te.budget_line_id,
            (loggedByBLMap.get(te.budget_line_id) ?? 0) + seconds / 3600,
          );
        }
      }

      type AssignmentRow = {
        id: string;
        budget_line_id: string | null;
        hours: number | null;
        label: string | null;
        task_status: string;
        rejection_reason: string | null;
        estimated_deadline: string | null;
        notes: string | null;
        budget_lines: {
          id: string;
          description: string | null;
          phase: string | null;
          project_id: string | null;
          projects: { id: string; name: string; project_status: string | null; is_internal: boolean | null; project_type: string | null; production_lead_id: string | null } | null;
        } | null;
      };
      // VIB-1967: hvor mange tildelinger brukeren har per budsjettlinje. Er det
      // nøyaktig én, vet vi hvilken tildeling en ukoblet føring hører til.
      // Er det flere (sub-tasks via label), kan vi ikke gjette.
      const assignmentsPerBL = new Map<string, number>();
      for (const a of (assignments ?? []) as unknown as AssignmentRow[]) {
        if (a.budget_line_id) {
          assignmentsPerBL.set(a.budget_line_id, (assignmentsPerBL.get(a.budget_line_id) ?? 0) + 1);
        }
      }

      const myTasks: MyTask[] = ((assignments ?? []) as unknown as AssignmentRow[]).map((a) => {
        const bl = a.budget_lines;
        const projectName = bl?.projects?.name ?? '—';
        const fallbackDesc = bl?.description ?? '—';
        const description = a.label ?? fallbackDesc; // VIB-671: label fallback
        const phase = bl?.phase ?? '';
        const projectId = bl?.project_id ?? '';
        const projectStatus = bl?.projects?.project_status ?? '';
        const isInternal = bl?.projects?.is_internal ?? false;
        const projectType = bl?.projects?.project_type ?? null;
        const budgetLineId = a.budget_line_id ?? '';

        // VIB-671: prioritize assignment_id-matching. Fallback to budget_line_id
        // only if no entries on this bl have assignment_id set (pre-VIB-671 data).
        let logged: number;
        // Sant når `logged` allerede er en linje-sum, som per definisjon
        // inkluderer de ukoblede føringene.
        let orphansAlreadyCounted = false;
        if (loggedByAssignmentMap.has(a.id)) {
          logged = loggedByAssignmentMap.get(a.id) ?? 0;
        } else if (budgetLineId && !blsWithAssignmentEntries.has(budgetLineId)) {
          logged = loggedByBLMap.get(budgetLineId) ?? 0;
          orphansAlreadyCounted = true;
        } else {
          logged = 0;
        }

        // VIB-1967: den tredje grenen ga tidligere 0 og tapte føringer uten
        // assignment_id stille, så snart ÉN annen føring på linjen var koblet.
        // Timene finnes, de er bare ukoblede (jf. «Ny registrering»-lekkasjen).
        // Legg dem på tildelingen når tilknytningen er utvetydig. Har brukeren
        // flere tildelinger på samme linje, lar vi det stå — da ville vi gjettet.
        if (
          !orphansAlreadyCounted &&
          budgetLineId &&
          (assignmentsPerBL.get(budgetLineId) ?? 0) === 1
        ) {
          logged += orphanByBLMap.get(budgetLineId) ?? 0;
        }

        return {
          id: a.id,
          assignment_id: a.id, // VIB-1967: eksplisitt, se MyTask-doccen
          budget_line_id: budgetLineId,
          project_id: projectId,
          task_description: description,
          task_status: a.task_status,
          phase,
          project_name: projectName,
          assigned_hours: Number(a.hours ?? 0),
          // Keep full precision — formatHours() rounds to whole minutes for
          // display. Rounding to 1 decimal here turned 11.25h (11t15m) into
          // 11.3h (11t18m) in Mine oppgaver "brukt" (feedback 24.06, Helge).
          logged_hours: logged,
          rejection_reason: a.rejection_reason ?? undefined,
          project_status: projectStatus,
          estimated_deadline: a.estimated_deadline ?? undefined,
          notes: a.notes ?? undefined,
          is_internal: isInternal,
          project_type: projectType,
          is_project_lead: !!userId && bl?.projects?.production_lead_id === userId,
        };
      }).filter((t) => {
        // Hide approved tasks from completed/cancelled projects
        if (t.task_status === 'approved' && ['completed', 'cancelled'].includes(t.project_status ?? '')) {
          return false;
        }
        return true;
      });

      const taskItems: TaskItem[] = myTasks.map(mt => ({
        id: mt.id,
        description: mt.task_description,
        phase: mt.phase,
        projectName: mt.project_name,
        assignedHours: mt.assigned_hours,
        loggedHours: mt.logged_hours,
        status: mt.task_status,
      })).sort((a, b) => {
        const order = { in_progress: 0, completed: 1, not_started: 2 };
        return (order[a.status as keyof typeof order] ?? 3) - (order[b.status as keyof typeof order] ?? 3);
      });

      // For backwards compatibility, fetch internal projects
      const { data: projects } = await supabase
        .from('projects')
        .select('id, name')
        .eq('is_internal', true)
        .eq('is_active', true)
        .is('deleted_at', null);

      // VIB-909: reisetid-eligible active projects (free logging, no assignment)
      const { data: reiseProjects } = await supabase
        .from('projects')
        .select('id, name, project_code')
        .eq('project_status', 'active')
        .eq('bills_travel_per_medgatt', true)
        .is('deleted_at', null)
        .order('project_code');

      return {
        tasks: myTasks,
        taskItems,
        internalProjects: projects ?? [],
        reiseProjects: reiseProjects ?? [],
      };
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });
}
